import React, { useState, useEffect, useMemo } from "react";
import Header from "../header/Header";
import Sidebar from "../Sidebar/Sidebar";
import LapIcon from "../../assets/lapIcon.svg";
import "./Notification.css";
import { Button, Empty, message } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  getAllNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from "../../services/ApiService";
import formatRelativeTime from "../../utils/formatRelativeTime";
import { notifyNotificationsRead } from "../../utils/notificationEvents";

// Groups a flat list of notifications (newest first, as GET
// /get-all-notification returns them) into Today / Yesterday / Earlier
// sections, and collapses identical (title + message) duplicates into
// one row with a "xN" count -- the real problem reported 2026-10-07:
// the same event can legitimately fire more than once (fan-out,
// re-runs, etc.), producing a wall of pixel-identical rows otherwise.
// A group counts as unread if ANY underlying instance is, and carries
// every underlying id so marking it read marks all of them.
const groupNotifications = (notifications) => {
  const groupsByKey = new Map();
  for (const item of notifications) {
    const key = `${item.title}|${item.message}`;
    const existing = groupsByKey.get(key);
    if (!existing) {
      groupsByKey.set(key, { ...item, count: 1, ids: [item.id], anyUnread: item.status === 1 });
    } else {
      existing.count += 1;
      existing.ids.push(item.id);
      existing.anyUnread = existing.anyUnread || item.status === 1;
      // Notifications arrive newest-first already, so the first
      // occurrence seen per key is already the most recent -- its
      // display fields (timestamp, link_url) are the ones kept.
    }
  }
  const deduped = [...groupsByKey.values()];

  const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const today = startOfDay(new Date());
  const yesterday = today - 86400000;

  const sections = { Today: [], Yesterday: [], Earlier: [] };
  for (const item of deduped) {
    const itemDay = item.created_date ? startOfDay(new Date(item.created_date)) : null;
    if (itemDay === today) sections.Today.push(item);
    else if (itemDay === yesterday) sections.Yesterday.push(item);
    else sections.Earlier.push(item);
  }
  return sections;
};

const SECTION_LABELS = ["Today", "Yesterday", "Earlier"];

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await getAllNotifications();
      if (res.responseCode === 200) {
        setNotifications(res.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const sections = useMemo(() => groupNotifications(notifications), [notifications]);
  const unreadTotal = notifications.filter((n) => n.status === 1).length;

  const markGroupRead = async (group) => {
    if (!group.anyUnread) return;
    try {
      await Promise.all(group.ids.map((id) => markNotificationRead(id)));
      setNotifications((prev) =>
        prev.map((n) => (group.ids.includes(n.id) ? { ...n, status: 0 } : n))
      );
      notifyNotificationsRead();
    } catch (error) {
      console.error("Failed to mark notification(s) read:", error);
    }
  };

  const handleSelect = (group) => {
    setSelectedGroup(group);
    markGroupRead(group);
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      const res = await markAllNotificationsRead();
      if (res.responseCode === 200) {
        setNotifications((prev) => prev.map((n) => ({ ...n, status: 0 })));
        notifyNotificationsRead();
        message.success("All notifications marked as read");
      } else {
        message.error(res.responseMessage || "Something went wrong");
      }
    } catch (error) {
      console.error("Failed to mark all as read:", error);
      message.error("Something went wrong");
    } finally {
      setMarkingAll(false);
    }
  };

  const renderGroup = (group) => (
    <div
      key={group.ids.join("-")}
      className="notification-box"
      onClick={() => handleSelect(group)}
      style={{
        cursor: "pointer",
        backgroundColor: group.anyUnread ? "white" : "#F6F7FB",
        borderLeft: group.anyUnread ? "3px solid #4A3AFF" : "3px solid transparent",
        position: "relative",
      }} 
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: "0.9rem", fontWeight: group.anyUnread ? 700 : 500 }}>
          {group.title || "Notification"}
          {group.count > 1 && (
            <span
              style={{
                marginLeft: "0.5rem",
                fontSize: "0.72rem",
                fontWeight: 600,
                color: "#787486",
                backgroundColor: "#EFF0F6",
                borderRadius: "1rem",
                padding: "0.05rem 0.5rem",
              }}
            >
              ×{group.count}
            </span>
          )}
        </span>
        {group.anyUnread && (
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: "#4A3AFF",
              flexShrink: 0,
            }}
          />
        )}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: "0.8rem",
          color: "#787486",
          marginTop: 2,
        }}
      >
        <span>{group.message}</span>
        <span style={{ flexShrink: 0, marginLeft: "1rem" }}>
          {formatRelativeTime(group.created_date)}
        </span>
      </div>
    </div>
  );

  const hasAny = notifications.length > 0;

  return (
    <div className="main-box">
      <Sidebar activeId={0} />
      <main className="main-content">
        <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
          <Header />
          <div style={{ height: "92vh", padding: "1.5rem", display: "flex", gap: "1rem" }}>
            <div style={{ flex: 1.4, display: "flex", flexDirection: "column", minWidth: 0 }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "0.75rem",
                }}
              >
                <span style={{ fontWeight: 600, fontSize: "1rem" }}>
                  Notifications{unreadTotal > 0 ? ` (${unreadTotal} unread)` : ""}
                </span>
                <Button
                  size="small"
                  icon={<CheckOutlined />}
                  onClick={handleMarkAllRead}
                  loading={markingAll}
                  disabled={unreadTotal === 0}
                >
                  Mark all as read
                </Button>
              </div>
              <div
                className="all-notifications custom-scrollbar"
                style={{ flex: 1, overflowY: "auto" }}
              >
                {!loading && !hasAny && (
                  <Empty description="No notifications yet" style={{ marginTop: "3rem" }} />
                )}
                {SECTION_LABELS.map((label) =>
                  sections[label].length > 0 ? (
                    <div key={label} style={{ marginBottom: "1.25rem" }}>
                      <div
                        style={{
                          fontSize: "0.78rem",
                          fontWeight: 700,
                          color: "#A0A0A0",
                          textTransform: "uppercase",
                          letterSpacing: "0.03em",
                          padding: "0.4rem 0",
                        }}
                      >
                        {label}
                      </div>
                      {sections[label].map(renderGroup)}
                    </div>
                  ) : null
                )}
              </div>
            </div>

            <div className="notification-content" style={{ flex: 1, minWidth: 0 }}>
              {selectedGroup ? (
                <div className="notification-item">
                  <div style={{ fontWeight: 600, fontSize: "1.05rem" }}>{selectedGroup.title}</div>
                  <span style={{ fontSize: "0.8rem", opacity: "50%" }}>
                    {formatRelativeTime(selectedGroup.created_date)}
                    {selectedGroup.count > 1 && ` · occurred ${selectedGroup.count} times`}
                  </span>
                  <p style={{ fontSize: "0.9rem", marginTop: "0.5rem", lineHeight: 1.5 }}>
                    {selectedGroup.message}
                  </p>
                  {selectedGroup.link_url && (
                    <Button
                      type="primary"
                      style={{ marginTop: "0.5rem", alignSelf: "flex-start" }}
                      onClick={() => navigate(selectedGroup.link_url)}
                    >
                      Go to related page
                    </Button>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    height: "36vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <img src={LapIcon} alt="lap-icon" width={200} />
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Notifications;
